<script setup>
import { inject, onBeforeUnmount, provide, watch, computed, ref } from 'vue'
import { useEditor, EditorContent, VueNodeViewRenderer } from '@tiptap/vue-3'
import Mention from '@tiptap/extension-mention'
import { mentionItems } from './mentionSuggestion.js'
import { DictationInterim, DictationPending, insertDictated, resolvePending, setInterim, settlePending } from '@addictivegroup/addictive-dictation/tiptap'
import { AutoCorrect, ChangeMarks, FieldValues, Figure, SpellMarks, cleanPastedHtml, refreshDecorations, textExtensions } from '@addictivegroup/addictive-richtext/schema'
import { autoCorrecting, correctionItems, headingStyles, namedColors, pxToMm, spellingMenuItems, textMenuItems } from '@addictivegroup/addictive-richtext/core'
import { FieldsMenu, FigurePanel, FormattingToolbar, ImageButton, TextContextMenu, editorClipboard, suggestionMenu, trackActiveEditor, wordAt } from '@addictivegroup/addictive-richtext/vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import { placeholderFields } from '@/lib/placeholders.js'
import { offerChangeMarks, offerFieldValues } from '@/lib/richtext.js'
import { PageGaps, setPageGaps } from './documentExtensions.js'
import { spelling } from '@/lib/spelling.js'
import { useSession } from '@/stores/session.js'
import { unitKey } from '@/components/offers/canvas/changes.js'
import DocFigureView from './DocFigureView.vue'

/**
 * The one rich text editor (D-44 notes, D-58 offer sections, WYSIWYG like a word processor).
 * Value is a TipTap/ProseMirror JSON document — the format Core stores and renders.
 */
const props = defineProps({
  modelValue: { type: [Object, null], default: null },
  placeholder: { type: String, default: '' },
  mentions: Boolean,
  tables: Boolean,
  // A record's text (notes, descriptions; D-232): colours and highlights from the theme's few,
  // pasted ones matched to them; the offers' texts keep their own colours (the template's).
  palette: Boolean,
  placeholders: { type: Array, default: () => [] }, // ["account.name", …] offered in the toolbar
  readonly: Boolean,
  compact: Boolean,
  minHeight: { type: String, default: '6rem' },
  dictation: Boolean, // dictated text shows in the editor as it is said (D-146)
  // Pictures (D-148): async (file) => {url, width, height}; a figure with a caption and a source.
  images: { type: Function, default: null },
  // On the offer editor's page (D-150): no frame and no toolbar of its own (the page's toolbar
  // works on the text being edited), the document's paragraph classes, pictures as figures.
  variant: { type: String, default: 'field' },
  flow: { type: String, default: null }, // the key the page's pagination finds this text by
  placeholderValues: { type: Object, default: null },
  figures: { type: Object, default: null }, // {start, labels: {figure, source}}
  // What changed since the latest revision (D-154): {keys, removed}, or null.
  changes: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'submit', 'selection'])

const onPage = props.variant === 'document'
// No empty paragraph added after a closing list on the page (D-191): the text holds none, so the
// print shows none — a line the editor would show and the PDF not. The gap cursor stands there.
// Paragraph styles and colours kept in every editor, so that editing a text never loses them.
const extensions = textExtensions({ placeholder: () => props.placeholder, colors: props.palette ? 'named' : 'css', tables: props.tables, trailingNode: !onPage, paragraphFallback: onPage ? 'body' : null })
if (props.dictation) extensions.push(DictationPending, DictationInterim)
if (props.images || onPage) {
  // Pictures as figures (D-148), drawn on the offer's page as it prints them.
  const figure = onPage ? Figure.extend({ addNodeView: () => VueNodeViewRenderer(DocFigureView) }) : Figure
  extensions.push(figure.configure({ inline: false, HTMLAttributes: { class: 'figure-image' } }))
}
if (onPage) extensions.push(FieldValues.configure(offerFieldValues(() => props.placeholderValues || {})), ChangeMarks.configure(offerChangeMarks(() => props.changes, unitKey, () => props.placeholderValues || {})))
// The spelling, ours (D-208), the browser's off: on the offer's page in its language, elsewhere
// in the panel's; a word wrong underlined, put right from the menu under the right button.
const docCanvas = inject('docCanvas', null)
const { t, locale } = useI18n()
const spellLang = () => (onPage
  ? (docCanvas && !docCanvas.readonly.value && ['pl', 'en'].includes(docCanvas.lang.value) ? docCanvas.lang.value : null)
  : (props.readonly ? null : locale.value === 'en' ? 'en' : 'pl'))
extensions.push(SpellMarks.configure({ lang: spellLang, isWrong: spelling.isWrong, className: 'spell-wrong' }))
// Obvious slips put right as they are typed (D-208): Backspace right after, or the right button
// on the word, brings back what was typed; off in one's settings.
const session = useSession()
extensions.push(AutoCorrect.configure({ lang: spellLang, ...autoCorrecting(spelling, { enabled: () => session.me?.settings?.autocorrect !== false }), className: 'autocorrected' }))
// Where a paragraph of it crosses a page (D-191): set by the page's pagination, found by `flow`.
const gaps = []
const pushed = []
if (onPage && props.flow) extensions.push(PageGaps)
// The figures' numbering and labels, for the pictures drawn on the page.
provide('docFigures', computed(() => props.figures))
if (props.mentions) {
  extensions.push(Mention.configure({
    HTMLAttributes: { class: 'mention' },
    renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
    suggestion: suggestionMenu({ items: mentionItems, attrs: { 'data-mention-list': '' } }),
  }))
}

// The text as this editor last sent it.
let sent
const editor = useEditor({
  content: props.modelValue || null,
  editable: !props.readonly,
  extensions,
  editorProps: {
    attributes: onPage ? { class: 'doc-text', spellcheck: spellLang() ? 'false' : 'true' } : { class: 'tiptap prose-crm px-3 py-2', spellcheck: 'false', style: `min-height:calc(${props.minHeight} * var(--zoom-text))` },
    // Pasted from e-mail or Word: Office's own markup out first (D-220).
    transformPastedHTML: (html) => cleanPastedHtml(html, { record: props.palette }),
    handleKeyDown: (_view, event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        emit('submit')
        return true
      }
      return false
    },
  },
  onUpdate: ({ editor: e }) => {
    sent = e.isEmpty ? null : e.getJSON()
    emit('update:modelValue', sent)
  },
  // The selected text, for "this was meant to be…" in dictation (D-145).
  onSelectionUpdate: ({ editor: e }) => {
    const { from, to } = e.state.selection
    emit('selection', from === to ? '' : e.state.doc.textBetween(from, to, ' ').trim())
  },
})

watch(() => props.modelValue, (value) => {
  // What this editor itself just sent, back: nothing to compare (a long text, at every key).
  if (!editor.value || (value !== null && value === sent)) return
  const current = JSON.stringify(editor.value.isEmpty ? null : editor.value.getJSON())
  if (current === JSON.stringify(value || null)) return
  editor.value.commands.setContent(value || null, { emitUpdate: false })
  // The text replaced whole (saved, discarded, put together): the pages' room in it went with it —
  // forgotten here too, so the next measure puts it back instead of taking it for still there.
  gaps.splice(0)
  pushed.splice(0)
})
watch(() => props.readonly, (ro) => editor.value?.setEditable(!ro))
// More words known as checked: their marks drawn.
watch(spelling.version, () => refreshDecorations(editor.value))
// Each by itself: one array of them is new at every render of the page around.
watch([() => props.placeholderValues, () => props.changes], () => refreshDecorations(editor.value))
// The page's toolbar works on the text being edited, with its pictures and fields.
trackActiveEditor(editor, () => ({ images: props.images, placeholders: props.placeholders }))
const flows = inject('docFlows', null)
if (flows && onPage && props.flow) {
  flows.set(props.flow, {
    editor: () => editor.value,
    // The pagination's room in this text (D-191): set when it changed; the editor carries it along as the text changes.
    setGaps(list, pushes = []) {
      if (JSON.stringify(list) === JSON.stringify(gaps) && JSON.stringify(pushes) === JSON.stringify(pushed)) return false
      gaps.splice(0, gaps.length, ...list)
      pushed.splice(0, pushed.length, ...pushes)
      setPageGaps(editor.value, gaps, pushed)
      return true
    },
  })
  onBeforeUnmount(() => flows.delete(props.flow))
}
onBeforeUnmount(() => editor.value?.destroy())

// The toolbar's formatting (addictive-richtext), in the order it stands, by groups (D-232, D-251):
// as many as its width holds stand in it, the rest are under "⋯"; the colours always stand.
const toolbarItems = computed(() => [
  'bold', 'italic', 'underline', ...(props.palette ? ['colors'] : []), '|', 'bullets', 'numbers', 'link', '|', ...(props.tables ? ['tableTools'] : []), '|',
  'strike', 'quote', 'code', 'rule', ...(props.tables ? ['table'] : []), '|', ...(props.compact ? [] : ['left', 'center', 'right']), '|', 'clearFormatting',
])
const styles = headingStyles()
const palette = props.palette ? namedColors() : null
// The same under the right button (Shift: the browser's own menu), a word spelled wrong first.
const frame = ref(null)
async function spellingFor(event, target) {
  const lang = spellLang()
  const found = lang && wordAt(event, target, editor.value)
  if (!found) return []
  const corrected = correctionItems(editor.value, found.pos)
  return corrected.length ? corrected : spellingMenuItems({ word: found.word, lang, checker: spelling, replace: found.replace }).catch(() => [])
}
const menuFor = async (event, target) => [...(await spellingFor(event, target)), ...formattingMenu()]
const formattingMenu = () => textMenuItems(editor.value, ['clipboard', '|', 'bold', 'italic', 'underline', '|', 'bullets', 'numbers', 'link', ...(props.tables ? ['table'] : []), ...(props.compact ? [] : ['align']), '|', 'clearFormatting'], { aligns: ['left', 'center', 'right'], clipboard: editorClipboard(editor.value) })

// A picture (D-148): uploaded by the host, a figure as wide as it is up to 600 px (else the default); its caption and source edited below the text.
const pictureWidth = (picture) => (picture.width && picture.width < 600 ? pxToMm(picture.width) : null)

const meta = useMeta()
// The fields named and grouped (D-171): the offer's own fields among them.
const fieldList = computed(() => placeholderFields(props.placeholders, t, meta))
defineExpose({
  focus: () => editor.value?.commands.focus(),
  clear: () => editor.value?.commands.clearContent(true),
  // The selection replaced by a word (a term taught in place of what dictation wrote).
  replaceSelection: (text) => editor.value?.chain().focus().insertContent(text).run(),
  // Dictated text goes where the caret is, with a space before it when the text runs on, and
  // a capital letter when it starts a sentence (recognition on the device writes lower case).
  // A phrase the accurate model is still writing again is marked as waiting (`pending`: its id).
  insert: (text, options) => insertDictated(editor.value, text, options),
  // What is being said, at the caret; not part of the note.
  interim: (text) => setInterim(editor.value, text),
  // A waiting phrase gets its better text.
  resolve: (id, text) => resolvePending(editor.value, id, text),
  // Waiting phrases keep the text they have.
  settle: () => settlePending(editor.value),
})
</script>

<template>
  <EditorContent v-if="onPage" :editor="editor" class="doc-flow" :data-flow="flow" />
  <div v-else ref="frame" :class="['field-box rounded-lg border border-default', readonly ? 'bg-[var(--surface-2)]' : 'surface focus-within:border-[var(--focus)]']">
    <FormattingToolbar v-if="!readonly && editor" :editor="editor" :items="toolbarItems" :styles="styles" :palette="palette" overflow variant="field">
      <template #tools>
        <ImageButton v-if="images" data-fixed :editor="editor" :images="images" :width="pictureWidth" />
      </template>
      <template #end>
        <FieldsMenu data-fixed :editor="editor" :fields="fieldList" />
      </template>
    </FormattingToolbar>
    <EditorContent :editor="editor" />
    <!-- the selected picture's caption, source and width -->
    <FigurePanel v-if="images && !readonly" :editor="editor" />
    <TextContextMenu :root="frame" within=".ProseMirror" :resolve="menuFor" testid="rte-context" />
  </div>
</template>
